import React from 'react';
import { Form, Input, Button, InputNumber, Switch, Space, Row, Col, Divider } from 'antd';
import { WCCustomer, CustomerContact } from '../../types/wordpress';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';

interface CustomerFormProps {
    initialValues?: WCCustomer | null;
    onSubmit: (values: Partial<WCCustomer>) => void;
    onCancel: () => void;
}

const CustomerForm: React.FC<CustomerFormProps> = ({ initialValues, onSubmit, onCancel }) => {
    const [form] = Form.useForm();

    const onFinish = (values: any) => {
        onSubmit({
            ...values,
            order_profit_percentage: Number(values.order_profit_percentage),
            item_profit_percentage: Number(values.item_profit_percentage),
            discount_percentage: Number(values.discount_percentage),
            cashback_percentage: Number(values.cashback_percentage),
            contacts: values.contacts?.map((contact: CustomerContact, index: number) => ({
                ...contact,
                is_default: values.defaultContactIndex === index
            })) || []
        });
    };

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
                ...initialValues,
                defaultContactIndex: initialValues?.contacts?.findIndex(c => c.is_default) || 0
            }}
        >
            <Divider>Basic Information</Divider>
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item name="first_name" label="Customer Name" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item name="email" label="Primary Email" rules={[{ type: 'email' }]}>
                        <Input placeholder="Will use default contact email if empty" />
                    </Form.Item>
                </Col>
            </Row>

            <Divider>Contact Information</Divider>
            <Form.Item label="Contacts">
                <Form.List name="contacts">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map((field, index) => (
                                <Row key={field.key} gutter={16} style={{ marginBottom: 16 }}>
                                    <Col span={5}>
                                        <Form.Item {...field} name={[field.name, 'contact_name']} rules={[{ required: true }]}>
                                            <Input placeholder="Contact Name" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={5}>
                                        <Form.Item {...field} name={[field.name, 'phone_number']}>
                                            <Input placeholder="Phone" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={5}>
                                        <Form.Item {...field} name={[field.name, 'email']} rules={[{ type: 'email' }]}>
                                            <Input placeholder="Email" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={5}>
                                        <Form.Item {...field} name={[field.name, 'web_site']}>
                                            <Input placeholder="Website" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Space>
                                            <Form.Item
                                                {...field}
                                                name={[field.name, 'is_default']}
                                                valuePropName="checked"
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Switch
                                                    checkedChildren="Default"
                                                    unCheckedChildren={<span className="text-gray-700">Not Default</span>}
                                                    className="bg-gray-300 ant-switch-checked:bg-blue-500"
                                                    onChange={(checked) => {
                                                        if (checked) {
                                                            const contacts = form.getFieldValue('contacts');
                                                            contacts.forEach((contact: any, i: number) => {
                                                                if (i !== index) {
                                                                    contact.is_default = false;
                                                                }
                                                            });
                                                            form.setFieldsValue({ contacts });
                                                        }
                                                    }}
                                                />
                                            </Form.Item>
                                            <MinusCircleOutlined onClick={() => remove(field.name)} />
                                        </Space>
                                    </Col>
                                </Row>
                            ))}
                            <Form.Item>
                                <Button
                                    type="dashed"
                                    onClick={() => add({ is_default: fields.length === 0 })}
                                    block
                                    icon={<PlusOutlined />}
                                >
                                    Add Contact
                                </Button>
                            </Form.Item>
                        </>
                    )}
                </Form.List>
            </Form.Item>

            <Divider>Pricing Settings</Divider>
            <Row gutter={16}>
                <Col span={6}>
                    <Form.Item name="order_profit_percentage" label="Order Profit %" rules={[{ type: 'number' }]}>
                        <InputNumber min={0} max={100} />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item name="item_profit_percentage" label="Item Profit %" rules={[{ type: 'number' }]}>
                        <InputNumber min={0} max={100} />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item name="discount_percentage" label="Discount %" rules={[{ type: 'number' }]}>
                        <InputNumber min={0} max={100} />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item name="cashback_percentage" label="Cashback %" rules={[{ type: 'number' }]}>
                        <InputNumber min={0} max={100} />
                    </Form.Item>
                </Col>
            </Row>

            <Divider>Address Information</Divider>
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item name="address" label="Address">
                        <Input />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item name="city" label="City">
                        <Input />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="state" label="State">
                        <Input />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="zip_code" label="ZIP Code">
                        <Input />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="fda" label="FDA Number">
                        <Input />
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item>
                <div style={{ textAlign: 'right' }}>
                    <Button onClick={onCancel} style={{ marginRight: 8 }}>
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit">
                        {initialValues ? 'Update' : 'Create'} Customer
                    </Button>
                </div>
            </Form.Item>
        </Form>
    );
};

export default CustomerForm;
